import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export const LIA_ENTRY_EVENT = 'laniakea:lia-open';

export interface LiaEntryRequest {
  prompt?: string;
  modelDepth?: number;
  autoSend?: boolean;
  openAttachment?: boolean;
  liveCall?: boolean;
  focusComposer?: boolean;
  attachmentUrl?: string;
  attachmentName?: string;
}

/**
 * One privacy-safe doorway into LIA from every app surface.
 *
 * User prompts travel in Angular's transient navigation state (or an in-memory
 * event when Mycelium is already open), never in a URL or durable storage.
 */
@Injectable({ providedIn: 'root' })
export class LiaEntryService {
  constructor(private readonly router: Router) {}

  open(request: LiaEntryRequest = {}): Promise<boolean> {
    const prompt = String(request.prompt || '').trim().slice(0, 20_000);
    const state: Record<string, string | boolean> = {
      liaAgentMode: true,
      liaFocusComposer: request.focusComposer === true
        || (!prompt && !request.openAttachment && !request.liveCall),
    };

    if (prompt) state.liaPrompt = prompt;
    if (Number.isInteger(request.modelDepth) && request.modelDepth >= 0 && request.modelDepth <= 2) {
      state.liaModelDepth = String(request.modelDepth);
    }
    if (prompt && request.autoSend === true) state.liaAutoSend = true;
    if (request.openAttachment === true) state.liaOpenAttachment = true;
    if (request.attachmentUrl) state.liaAttachmentUrl = request.attachmentUrl;
    if (request.attachmentName) state.liaAttachmentName = request.attachmentName;
    if (request.liveCall === true) state.liaLiveCall = true;

    const currentPath = String(this.router.url || '').split(/[?#]/, 1)[0];
    if (currentPath === '/mycelium' && typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent(LIA_ENTRY_EVENT, { detail: state }));
      return Promise.resolve(true);
    }

    return this.router.navigate(['/mycelium'], { state });
  }

  openComposer(): Promise<boolean> {
    return this.open({ focusComposer: true });
  }

  openAttachment(): Promise<boolean> {
    return this.open({ openAttachment: true });
  }

  openLiveCall(): Promise<boolean> {
    return this.open({ liveCall: true });
  }

  submitPrompt(prompt: string, modelDepth = 0): Promise<boolean> {
    return this.open({ prompt, autoSend: true, modelDepth });
  }
}
